/**
 * Status Overlay Component
 * Displays status indicators overlaid on canvas objects
 * and applies visual changes to elements based on their status
 */

import { useEffect, useMemo, useState } from 'react';
import { dia } from '@joint/core';
import { CctvStatusIndicator } from './CctvStatusIndicator';
import { useStatusStore } from '../model/statusStore';
import type { LightingStatus } from '../types/status';
import { log, LogCategory } from '../../../map-editor-shared/lib/logger';
import { routeLayerToCategory } from '../../../map-editor-shared/lib/layerUtils';
import styles from './StatusOverlay.module.css';

interface StatusOverlayProps {
  /** JointJS graph instance */
  graph: dia.Graph;
  /** JointJS paper instance for coordinate mapping */
  paper: dia.Paper;
}

/**
 * Status Overlay
 * Renders status indicators positioned over map objects
 *
 * Usage in EditorPage:
 * ```tsx
 * import { StatusOverlay, useStatusStore, statusService } from '../../../map-editor-features/status';
 *
 * // In component:
 * const { connect, disconnect } = useStatusStore();
 *
 * useEffect(() => {
 *   // Initialize with object IDs
 *   const cctvIds = graph.getElements()
 *     .filter(el => el.get('type') === 'Cctv')
 *     .map(el => el.id);
 *   const parkingIds = graph.getElements()
 *     .filter(el => el.get('type') === 'ParkingLocation')
 *     .map(el => el.id);
 *
 *   statusService.initialize(cctvIds, parkingIds);
 *   connect();
 *
 *   return () => disconnect();
 * }, [graph]);
 *
 * // Render:
 * {graph && paper && (
 *   <StatusOverlay graph={graph} paper={paper} />
 * )}
 * ```
 */
/**
 * Extract the lights cctvName from a CCTV graph element.
 * The name lives in element.get('data').properties.name (set by the map editor).
 * Falls back to label text or element id.
 *
 * This is the **cctvName matching key**: `data.properties.name`
 */
function getCctvNameFromElement(element: dia.Element): string {
  const data = element.get('data') as
    | { properties?: { name?: string } }
    | undefined;
  const propName = data?.properties?.name;
  if (propName) return propName;
  const labelText = element.attr('label/text') as string | undefined;
  if (labelText) return labelText;
  return String(element.id);
}

/** 이름 정규화 — 맵 CCTV명과 이벤트 cctvName 의 대소문자/공백 차이를 흡수한다. */
function normalizeName(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * 라이브 JointJS 엘리먼트의 카테고리 — data.layer 우선, 없으면 data.type 폴백.
 * (zoneCctvRelations.elementCategory / mapAssetFix.cellCategory 와 동일 규칙)
 *
 * 실제 배치된 CCTV/주차 셀은 `el.get('type')` 가 'standard.Image'·'standard.Rectangle'
 * 이고, objectType 은 top-level 이 아니라 `data.objectType` 에 중첩되며, id 는 UUID 라서
 * 예전의 type/objectType/id-substring 필터로는 하나도 잡히지 않았다(맵에 glow·배지가
 * 안 뜨던 원인). data.layer 카테고리로 판정해야 한다.
 */
function elementCategory(element: dia.Element): string {
  const data = (element.get('data') as { layer?: unknown; type?: unknown }) || {};
  if (typeof data.layer === 'string' && data.layer) {
    const c = routeLayerToCategory(data.layer);
    if (c !== 'unknown') return c;
  }
  if (typeof data.type === 'string' && data.type) {
    return routeLayerToCategory(data.type);
  }
  return 'unknown';
}

/** CCTV(조명/단방향) 엘리먼트 판정. 카테고리 우선, 레거시 표기는 폴백으로 허용. */
function isCctvElement(element: dia.Element): boolean {
  const c = elementCategory(element);
  if (c === 'cctv' || c === 'warningCctv') return true;
  const id = String(element.id);
  const type = element.get('type') || element.get('objectType');
  return (
    type === 'Cctv' ||
    type === 'cctv' ||
    id.includes('cctv') ||
    id.includes('onepassreader') ||
    id.includes('reader')
  );
}

/** 주차면 엘리먼트 판정. 카테고리 우선, 레거시 표기는 폴백으로 허용. */
function isParkingElement(element: dia.Element): boolean {
  if (elementCategory(element) === 'parkingLocation') return true;
  const id = String(element.id);
  const type = element.get('type') || element.get('objectType');
  return (
    type === 'ParkingLocation' ||
    type === 'parkingLocation' ||
    type === 'parking' ||
    id.includes('parking')
  );
}

/**
 * 조명 종류(한국어 레이블) → glow RGB.
 * lightType 은 LightsSimulator 가 변환한 레이블(통로등/만공차등/충돌방지등/경광등)이다.
 */
function lightGlowRgb(lightType: string): [number, number, number] {
  if (lightType.includes('경광')) return [239, 68, 68]; // warning — red
  if (lightType.includes('충돌')) return [249, 115, 22]; // caution — orange
  if (lightType.includes('만공차')) return [52, 211, 153]; // parking — green
  return [255, 214, 10]; // aisle/dimming(통로등) — warm yellow
}

/** 유휴(절전) 통로등 기본 디밍 — 현장 통로등이 모션 없을 때 머무는 값(dimMin). */
const BASELINE_DIM = 20;

/**
 * 화면 밖 배지를 렌더에서 제외하기 위한 컬링 판정.
 * 엘리먼트 bbox 를 스크린 좌표로 변환해 뷰포트와 겹치지 않으면 true(=스킵).
 * margin 은 배지가 셀 모서리 바깥(위/오른쪽)으로 그려지는 여유분(스크린 px).
 * viewport 가 없거나 크기 0 이면(테스트 mock 등) 컬링을 생략한다.
 */
function isOutsideViewport(
  bbox: { x: number; y: number; width: number; height: number },
  scale: { sx: number; sy: number },
  translate: { tx: number; ty: number },
  viewport: { width: number; height: number } | null,
  margin: number,
): boolean {
  if (!viewport || viewport.width <= 0 || viewport.height <= 0) return false;
  const left = bbox.x * scale.sx + translate.tx;
  const top = bbox.y * scale.sy + translate.ty;
  const right = (bbox.x + bbox.width) * scale.sx + translate.tx;
  const bottom = (bbox.y + bbox.height) * scale.sy + translate.ty;
  return (
    right < -margin ||
    left > viewport.width + margin ||
    bottom < -margin ||
    top > viewport.height + margin
  );
}

/** 배지가 셀 모서리 밖으로 그려지는 여유분(스크린 px) — 컬링 경계가 너무 빡빡하지 않게. */
const BADGE_CULL_MARGIN = 64;

export function StatusOverlay({ graph, paper }: StatusOverlayProps) {
  // Force re-render when paper viewport changes (zoom/pan)
  const [, forceUpdate] = useState({});

  // Bumped only when the element *set* changes (add/remove/reset), not on every
  // attribute change or transform — so the CCTV filter below is not re-run on
  // every pan/zoom frame.
  const [elementVersion, setElementVersion] = useState(0);

  // CCTV(=조명) 엘리먼트만 오버레이가 필요하다. 주차면 점유는 셀 색상 변경으로
  // 표시하므로(아래 useEffect) 별도 배지를 그리지 않는다. getElements()+filter 는
  // 전체 셀 수만큼 비싸므로 엘리먼트 집합이 바뀔 때만(elementVersion) 재계산한다.
  const cctvElements = useMemo(
    () => graph.getElements().filter(isCctvElement),
    [graph, elementVersion],
  );

  // Get status data from store
  const { cctvStatuses, parkingStatuses, lightingStatuses, lightingBaseline } = useStatusStore();

  // Listen to paper transformations (zoom, pan) to update badge positions
  useEffect(() => {
    // Coalesce bursts of scale/translate events into one re-render per frame so a
    // rapid zoom/pan doesn't trigger a synchronous re-render of every badge per event.
    let rafId: number | null = null;

    const handleTransform = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        forceUpdate({});
      });
    };

    // Subscribe to paper transform events
    paper.on('scale', handleTransform);
    paper.on('translate', handleTransform);

    return () => {
      paper.off('scale', handleTransform);
      paper.off('translate', handleTransform);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [paper]);

  // Apply visual changes to elements based on status
  useEffect(() => {
    // Get floor context from graph metadata
    const floorId = graph.get('floorId');

    // Only log if there are actual status updates (not empty state)
    const hasParkingStatuses = Object.keys(parkingStatuses).length > 0;
    const hasCctvStatuses = Object.keys(cctvStatuses).length > 0;

    if (hasParkingStatuses || hasCctvStatuses) {
      log.debug(LogCategory.STATE, 'Applying visual status updates', {
        floorId: floorId || 'unknown',
        parkingStatuses: Object.keys(parkingStatuses).length,
        cctvStatuses: Object.keys(cctvStatuses).length,
      });
    }

    // Get fresh element lists from graph
    const allElements = graph.getElements();
    const currentParkingElements = allElements.filter(isParkingElement);
    const currentCctvElements = allElements.filter(isCctvElement);

    log.debug(LogCategory.STATE, 'Elements found', {
      parking: currentParkingElements.length,
      cctv: currentCctvElements.length,
    });

    let occupiedCount = 0;
    let emptyCount = 0;
    let notFoundCount = 0;

    // Update parking space colors based on occupancy
    currentParkingElements.forEach(element => {
      const elementId = String(element.id);

      // Create namespaced ID for lookup
      const namespacedId = floorId ? `${floorId}::${elementId}` : elementId;
      const status = parkingStatuses[namespacedId];

      if (status) {
        // Store original color if not already stored
        if (!element.get('originalColor')) {
          const currentColor = element.attr('body/fill') || '#4b5563'; // gray-600
          element.set('originalColor', currentColor);
        }

        // Change color based on occupancy
        if (status.occupied) {
          element.attr('body/fill', '#ef4444'); // red-500 when occupied
          element.attr('body/fillOpacity', 0.7);
          occupiedCount++;
        } else {
          // Restore original color when available
          const originalColor = element.get('originalColor') || '#4b5563';
          element.attr('body/fill', originalColor);
          element.attr('body/fillOpacity', 0.5);
          emptyCount++;
        }
      } else {
        notFoundCount++;
      }
    });

    log.debug(LogCategory.STATE, 'Parking visual updates', {
      occupied: occupiedCount,
      empty: emptyCount,
      notFound: notFoundCount,
    });

    // Update CCTV appearance based on connection status
    let connectedCount = 0;
    let disconnectedCount = 0;

    currentCctvElements.forEach(element => {
      const elementId = String(element.id);

      // Create namespaced ID for lookup
      const namespacedId = floorId ? `${floorId}::${elementId}` : elementId;
      const status = cctvStatuses[namespacedId];

      if (status) {
        if (!status.connected) {
          element.attr('root/data-cctv-disconnected', true);
          disconnectedCount++;
        } else {
          element.attr('root/data-cctv-disconnected', false);
          connectedCount++;
        }
      }
    });

    log.debug(LogCategory.STATE, 'CCTV visual updates', {
      connected: connectedCount,
      disconnected: disconnectedCount,
    });
  }, [graph, parkingStatuses, cctvStatuses]);

  // Listen for graph changes to re-render
  useEffect(() => {
    const handler = () => {
      // Force re-render when graph changes
      forceUpdate({});
    };
    // Recompute the memoized CCTV list only when the element set changes.
    const setChanged = () => setElementVersion(v => v + 1);

    graph.on('change', handler);
    graph.on('add', setChanged);
    graph.on('remove', setChanged);
    graph.on('reset', setChanged);
    return () => {
      graph.off('change', handler);
      graph.off('add', setChanged);
      graph.off('remove', setChanged);
      graph.off('reset', setChanged);
    };
  }, [graph]);

  // Get current paper transformations
  const scale = paper.scale();
  const translate = paper.translate();

  // 뷰포트 크기(스크린 px) — 화면 밖 조명 글로우는 렌더를 건너뛰어(컬링) 줌/팬을 가볍게 한다.
  // getComputedSize 미가용(테스트 mock 등)이나 0 이면 컬링을 생략하고 전부 그린다.
  const viewport =
    typeof paper.getComputedSize === 'function' ? paper.getComputedSize() : null;

  // Normalized lookup so map CCTV names match event cctvNames despite case/space drift.
  const lightingByNormalizedName = Object.values(lightingStatuses).reduce<
    Record<string, LightingStatus>
  >((acc, ls) => {
    acc[normalizeName(ls.cctvName)] = ls;
    return acc;
  }, {});

  const lightingFor = (element: dia.Element): LightingStatus | undefined => {
    const name = getCctvNameFromElement(element);
    return lightingStatuses[name] ?? lightingByNormalizedName[normalizeName(name)];
  };

  return (
    <div className={styles.statusOverlay}>
      {/* CCTV Status Indicators */}
      {cctvElements.map((element) => {
        const bbox = element.getBBox();
        // 화면 밖 CCTV 배지는 렌더 스킵(컬링) — 줌/팬 시 리렌더 비용 절감.
        if (isOutsideViewport(bbox, scale, translate, viewport, BADGE_CULL_MARGIN)) {
          return null;
        }
        // Position badge at top-right corner of CCTV icon
        // Calculate screen position using paper transformations
        const x = (bbox.x + bbox.width) * scale.sx + translate.tx;
        const y = bbox.y * scale.sy + translate.ty;
        const objectId = String(element.id);

        return (
          <div
            key={objectId}
            className={styles.statusPosition}
            style={{
              left: `${x}px`,
              top: `${y - 8 * scale.sy}px`,
              transform: `scale(${scale.sx})`,
              transformOrigin: 'top left',
            }}
          >
            <CctvStatusIndicator objectId={objectId} />
          </div>
        );
      })}

      {/* Lighting — 조명 위치(=CCTV)에 뜨는 빛 번짐(블룸).
          · 종류(범주) = 빛 색(통로등 노랑/경광 빨강/충돌 주황/만공차 초록)
          · 밝기(연속) = 번짐 크기 + 농도(밝을수록 크고 진하게, 어두우면 점에 가깝게)
          radial-gradient 1개라 box-shadow/blur 없이 GPU 합성만으로 끝나, 회색조 도면 위에서
          빛만 도드라진다. 숫자는 맵에 그리지 않고 호버 title 로만 노출(클러터 방지).
          Baseline: lightingBaseline ON 이면 모든 통로등 CCTV 를 유휴 20%(작고 은은한 노란 점)로 그린다.
          베이스라인 off + 이벤트 없음(에디터/뷰어)에서는 아무것도 그리지 않는다.
          화면 밖 CCTV 는 컬링으로 건너뛴다(줌/팬 시 렌더 비용 절감). */}
      {cctvElements.map(element => {
        const ls = lightingFor(element);
        const active = !!(ls && ls.on);
        if (!active && !lightingBaseline) return null;

        const lightType = active ? ls!.lightType : '통로등';
        const dimming = active ? ls!.dimming : BASELINE_DIM;
        const [r, g, b] = lightGlowRgb(lightType);
        const d = Math.min(1, Math.max(0, dimming / 100));

        const bbox = element.getBBox();
        const cx = (bbox.x + bbox.width / 2) * scale.sx + translate.tx;
        const cy = (bbox.y + bbox.height / 2) * scale.sy + translate.ty;
        // 빛 번짐 지름: 밝을수록 크게(아이콘의 1.3~3.9×). 어두우면 작아 점에 가깝다.
        const iconPx = Math.max(bbox.width, bbox.height, 24) * scale.sx;
        const bloomPx = iconPx * (1.3 + 2.6 * d);
        const radius = bloomPx / 2;

        // 뷰포트 밖이면 렌더 스킵(컬링). 크기 0/미가용이면 컬링 생략.
        if (
          viewport &&
          viewport.width > 0 &&
          viewport.height > 0 &&
          (cx + radius < 0 ||
            cx - radius > viewport.width ||
            cy + radius < 0 ||
            cy - radius > viewport.height)
        ) {
          return null;
        }

        // 밝기 = 빛의 농도(중심 알파). 색(rgb) = 종류. 가장자리는 투명으로 부드럽게 사라진다.
        const core = Math.min(1, 0.55 + 0.45 * d).toFixed(3);
        const mid = (0.3 + 0.5 * d).toFixed(3);
        const glow = `radial-gradient(circle, rgba(${r}, ${g}, ${b}, ${core}) 0%, rgba(${r}, ${g}, ${b}, ${mid}) 32%, rgba(${r}, ${g}, ${b}, 0) 70%)`;
        const name = getCctvNameFromElement(element);
        const title = `${name} · ${lightType || '조명'} · ${dimming}%`;

        return (
          <div
            key={`lighting-${String(element.id)}`}
            className={`${styles.lightingBloom} ${active ? styles.bloomActive : ''}`}
            style={{
              left: `${cx - radius}px`,
              top: `${cy - radius}px`,
              width: `${bloomPx}px`,
              height: `${bloomPx}px`,
              background: glow,
            }}
            title={title}
          />
        );
      })}

      {/* 주차면 점유는 셀 body/fill 색상 변경(위 useEffect)으로 표시하므로 오버레이
          배지를 그리지 않는다. ParkingStatusIndicator 는 항상 null 을 반환했어서,
          기존 루프는 주차면 수만큼(수백~수천) 빈 div 래퍼를 매 프레임 생성·순회하던
          순수 낭비였다 — 줌/팬 버벅임의 주원인이라 제거. */}
    </div>
  );
}
